import { styled } from "@mui/material";
import {
    memo,
    PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";

import { type IdentifiedWindowEntry, useDispatchWindows, windowsActions } from "contexts/windows";

import { VIEWER_INSET } from "./constants";
import {
    DOCK_EDGE_MARGIN,
    DOCK_LEFT,
    DOCK_MIN_HEIGHT,
    DOCK_RESIZE_HANDLE_HEIGHT,
    DOCK_SELF_COLLAPSE_TOLERANCE,
    DOCK_Z_INDEX,
} from "./dock/constants";
import { type DockDragController, useDockDragController } from "./dock/dockDragContext";
import { type DragGrabCapture, getContainerOrigin, positionFromPointer } from "./dock/dragGeometry";
import { useDockDragSource } from "./dock/useDockDragSource";
import { WindowDockedContext } from "./windowDockedContext";
import { WindowMinimizeContext } from "./windowMinimizeContext";
import { WindowPositionContext } from "./windowPositionContext";
import { WindowSizeContext } from "./windowSizeContext";

interface DragOutCapture extends DragGrabCapture {
    dragSize: { width: number; height: number };
}

const ResizeHandle = styled("div")`
    position: absolute;
    height: ${DOCK_RESIZE_HANDLE_HEIGHT}px;
    cursor: ns-resize;
    z-index: 2;
    touch-action: none;
    pointer-events: auto;
`;

function useLiveDragPositionOverride(
    controller: DockDragController,
    windowId: string,
): { x: number; y: number } | null {
    const isDockedDragTarget =
        controller.state.isDragging && controller.state.draggingId === windowId && controller.state.source === "docked";
    return useSyncExternalStore(
        controller.subscribeLivePosition,
        () => (isDockedDragTarget ? controller.getLivePosition() : null),
        () => null,
    );
}

export interface WindowSlotProps {
    entry: IdentifiedWindowEntry;
    onFocus: (windowId: string) => void;
    onClose: () => void;
    onToggleMinimize: () => void;
    onSetHeight: (height: number) => void;
    onSetMinimizedHeight: (height: number) => void;
    /** `undefined` releases a previously recorded self-collapse height. */
    onSetContentHeight: (height: number | undefined) => void;
    maxHeight: number;
    isLast: boolean;
    dockTop: number;
    dockHeight: number;
}

function WindowSlotImpl({
    entry,
    onFocus,
    onClose,
    onToggleMinimize,
    onSetHeight,
    onSetMinimizedHeight,
    onSetContentHeight,
    maxHeight,
    isLast,
    dockTop,
    dockHeight,
}: WindowSlotProps) {
    const { id, config, state } = entry;
    const dispatch = useDispatchWindows();
    const controller = useDockDragController();
    const isMinimized = state.minimized;
    const docked = state.docked;
    const dockable = config.dockable !== false;
    const WindowComponent = config.component;
    const captureRef = useRef<DragOutCapture | null>(null);
    const dragCleanupRef = useRef<(() => void) | null>(null);
    const [slotHostElement, setSlotHostElement] = useState<HTMLDivElement | null>(null);

    const liveWidth = useSyncExternalStore(controller.subscribeLiveWidth, controller.getLiveWidth, () => null);
    const effectiveDockWidth = liveWidth?.width ?? controller.dockWidth;

    const dockPosition = useMemo(
        () => ({
            x: DOCK_LEFT,
            y: DOCK_EDGE_MARGIN + dockTop,
        }),
        [dockTop],
    );
    const dockSize = useMemo(
        () => ({
            width: effectiveDockWidth,
            height: dockHeight,
        }),
        [dockHeight, effectiveDockWidth],
    );

    const liveDragPosition = useLiveDragPositionOverride(controller, id);
    const effectivePosition = liveDragPosition ?? state.position;

    const handleResizePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!docked || isMinimized) {
                return;
            }
            dragCleanupRef.current?.();

            const startY = e.clientY;
            const startH = dockHeight;
            const target = e.currentTarget;
            const pointerId = e.pointerId;
            target.setPointerCapture(pointerId);

            const onMove = (ev: PointerEvent) => {
                const delta = ev.clientY - startY;
                const next = Math.min(maxHeight, Math.max(DOCK_MIN_HEIGHT, startH + delta));
                controller.setLiveResize({ id, height: next });
            };
            const cleanup = () => {
                target.removeEventListener("pointermove", onMove);
                target.removeEventListener("pointerup", onUp);
                target.removeEventListener("pointercancel", onUp);
                if (target.hasPointerCapture(pointerId)) {
                    target.releasePointerCapture(pointerId);
                }
                const final = controller.getLiveResize();
                if (final && final.id === id) {
                    onSetHeight(final.height);
                }
                controller.setLiveResize(null);
                if (dragCleanupRef.current === cleanup) {
                    dragCleanupRef.current = null;
                }
            };
            const onUp = () => cleanup();

            target.addEventListener("pointermove", onMove);
            target.addEventListener("pointerup", onUp);
            target.addEventListener("pointercancel", onUp);
            dragCleanupRef.current = cleanup;
        },
        [controller, dockHeight, docked, id, isMinimized, maxHeight, onSetHeight],
    );

    useEffect(() => {
        return () => {
            dragCleanupRef.current?.();
            const pending = controller.getLiveResize();
            if (pending?.id === id) {
                controller.setLiveResize(null);
            }
        };
    }, [controller, id]);

    // Latest values for the measurement callbacks below — they outlive renders.
    const dockHeightRef = useRef(dockHeight);
    dockHeightRef.current = dockHeight;
    const contentHeightRef = useRef(state.dockedContentHeight);
    contentHeightRef.current = state.dockedContentHeight;
    // True once this docked stint has confirmed a genuine self-collapse (a window
    // that keeps rendering below the height the layout assigned it). Guards the
    // stale-pin release so an intentionally collapsed window isn't unpinned.
    const hasRefusedAssignedHeightRef = useRef(false);
    useEffect(() => {
        if (!docked) {
            hasRefusedAssignedHeightRef.current = false;
        }
    }, [docked]);

    // Measure the actual rendered height of every docked window and report it to
    // the store. When minimized it feeds the minimized-slot height; when expanded
    // it lets the dock layout detect a self-collapsing third-party window and
    // reserve only the height it paints (see useDockLayout's flex solver).
    useEffect(() => {
        if (!docked || !slotHostElement || typeof ResizeObserver === "undefined") {
            return;
        }

        const draggable = slotHostElement.querySelector<HTMLElement>(".react-draggable");
        if (!draggable) {
            return;
        }

        if (isMinimized) {
            const reportHeight = () => {
                const measured = draggable.getBoundingClientRect().height;
                if (measured > 0) {
                    onSetMinimizedHeight(measured);
                }
            };
            reportHeight();
            const observer = new ResizeObserver(reportHeight);
            observer.observe(draggable);
            return () => observer.disconnect();
        }

        // Expanded: a measurement below the assigned slot height means either a
        // self-collapsing third-party window (persists) or a mid-transition frame
        // (resolves once the new geometry paints — common right after dock/undock
        // in production hosts where paint timing differs). Recording a transition
        // frame would pin the slot at a bogus height with no recovery, so a
        // suspected collapse is only reported after it survives to the next
        // animation frame. Conversely, a window observed filling its assigned
        // height releases any recorded value so the flex solver can regrow it.
        let confirmFrame: number | null = null;
        const evaluate = () => {
            const measured = draggable.getBoundingClientRect().height;
            if (measured <= 0) {
                return;
            }
            const assigned = dockHeightRef.current;
            if (measured < assigned - DOCK_SELF_COLLAPSE_TOLERANCE) {
                if (confirmFrame != null) {
                    return;
                }
                confirmFrame = requestAnimationFrame(() => {
                    confirmFrame = null;
                    const confirmed = draggable.getBoundingClientRect().height;
                    if (confirmed > 0 && confirmed < dockHeightRef.current - DOCK_SELF_COLLAPSE_TOLERANCE) {
                        hasRefusedAssignedHeightRef.current = true;
                        onSetContentHeight(confirmed);
                    }
                });
            } else if (!hasRefusedAssignedHeightRef.current && contentHeightRef.current != null) {
                onSetContentHeight(undefined);
            }
        };

        evaluate();
        const observer = new ResizeObserver(evaluate);
        observer.observe(draggable);
        return () => {
            observer.disconnect();
            if (confirmFrame != null) {
                cancelAnimationFrame(confirmFrame);
            }
        };
    }, [docked, isMinimized, onSetContentHeight, onSetMinimizedHeight, slotHostElement]);

    const { onPointerDown: floatingPointerDown } = useDockDragSource({
        source: "floating",
        windowId: id,
        onDropInZone: (insertionIndex) => {
            dispatch(windowsActions.dockWindow(id, insertionIndex));
        },
    });

    const { onPointerDown: dockedPointerDown } = useDockDragSource({
        source: "docked",
        windowId: id,
        ignoreTargetSelector: `[data-testid="dock-resize-${id}"]`,
        onActivate: (ev) => {
            if (!docked) {
                return;
            }
            const containerOrigin = getContainerOrigin();
            const draggableRect = slotHostElement
                ?.querySelector<HTMLElement>(".react-draggable")
                ?.getBoundingClientRect();
            const dragBasePosition = draggableRect
                ? {
                      x: draggableRect.left - containerOrigin.x,
                      y: draggableRect.top - containerOrigin.y,
                  }
                : dockPosition;
            const dragSize = draggableRect
                ? {
                      width: draggableRect.width,
                      height: draggableRect.height,
                  }
                : dockSize;
            const capture: DragOutCapture = {
                grabOffset: {
                    x: ev.clientX - (containerOrigin.x + dragBasePosition.x),
                    y: ev.clientY - (containerOrigin.y + dragBasePosition.y),
                },
                containerOrigin,
                dragSize,
            };
            captureRef.current = capture;
            const livePos = positionFromPointer(ev.clientX, ev.clientY, capture);
            controller.setLivePosition(livePos);
            dispatch(windowsActions.undockWindow(id, livePos, { dragSize: capture.dragSize }));
        },
        onMove: (ev) => {
            const capture = captureRef.current;
            if (!capture) {
                return;
            }
            controller.setLivePosition(positionFromPointer(ev.clientX, ev.clientY, capture));
        },
        onDropInZone: (insertionIndex) => {
            captureRef.current = null;
            dispatch(windowsActions.dockWindow(id, insertionIndex));
        },
        onDropOutsideZone: () => {
            captureRef.current = null;
            const finalPos = controller.getLivePosition();
            if (finalPos) {
                dispatch(windowsActions.setWindowPosition(id, finalPos));
            } else {
                dispatch(windowsActions.clearDragSize(id));
            }
        },
    });

    const handleHeaderPointerDown = docked ? dockedPointerDown : dockable ? floatingPointerDown : undefined;

    const minimizeValue = useMemo(
        () => ({ minimized: isMinimized, onToggle: onToggleMinimize, isDocked: docked }),
        [docked, isMinimized, onToggleMinimize],
    );

    const bodyContent = useMemo(
        () => (
            <WindowMinimizeContext.Provider value={minimizeValue}>
                <WindowDockedContext.Provider value={docked}>
                    <WindowPositionContext.Provider value={docked ? dockPosition : (effectivePosition ?? null)}>
                        <WindowSizeContext.Provider value={docked ? dockSize : (state.dragSize ?? null)}>
                            <WindowComponent position={docked ? undefined : effectivePosition} onClose={onClose} />
                        </WindowSizeContext.Provider>
                    </WindowPositionContext.Provider>
                </WindowDockedContext.Provider>
            </WindowMinimizeContext.Provider>
        ),
        [WindowComponent, dockPosition, dockSize, docked, effectivePosition, minimizeValue, onClose, state.dragSize],
    );

    return (
        <div
            ref={setSlotHostElement}
            style={{
                position: "absolute",
                inset: VIEWER_INSET,
                zIndex: docked ? DOCK_Z_INDEX : state.zIndex,
                pointerEvents: "none",
            }}
            onPointerDownCapture={docked ? undefined : () => onFocus(id)}
            onPointerDown={handleHeaderPointerDown}
            data-testid={`${docked ? "docked" : "floating"}-window-${id}`}
        >
            {bodyContent}
            {docked && !isLast && !isMinimized && (
                <ResizeHandle
                    style={{
                        top: dockPosition.y + dockSize.height - DOCK_RESIZE_HANDLE_HEIGHT,
                        left: dockPosition.x + 8,
                        right: `calc(100% - ${dockPosition.x + dockSize.width - 8}px)`,
                    }}
                    onPointerDown={handleResizePointerDown}
                    data-testid={`dock-resize-${id}`}
                />
            )}
        </div>
    );
}

export const WindowSlot = memo(WindowSlotImpl);
